import { z } from 'zod';

import { riskProfileEnum } from '@/features/clients/types/schemas';
import {
  recordingSyncEnum,
  sessionCategoryEnum,
  telephonyDirectionEnum,
  type RedFlagFilter,
  type SessionCategory,
} from '@/features/telephony/types/schemas';
import { type ProjectKey } from '@/lib/projects';

import { concernEnum } from '../constants/concerns';

type CallDirection = z.infer<typeof telephonyDirectionEnum>;

/** BE sessionStatusEnum (sessionService) — 6 values, not 4. Spec missed no_show + rescheduled. */
export const sessionStatusEnum = z.enum([
  'scheduled',
  'in_progress',
  'completed',
  'cancelled',
  'no_show',
  'rescheduled',
]);
export type SessionStatus = z.infer<typeof sessionStatusEnum>;

export const sessionTypeEnum = z.enum(['new', 'follow_up', 'crisis']);
export type SessionType = z.infer<typeof sessionTypeEnum>;

export const sessionModeEnum = z.enum(['online', 'in_person', 'helpline']);
export type SessionMode = z.infer<typeof sessionModeEnum>;

/** How a session row originated. `instant` is the click-to-call path
 *  (portal_helpline live calls); `portal` covers scheduled bookings (campus
 *  and portal alike); `offline` is the backfill path. Instant is the only
 *  delivery flavour that skips the join window and blocks reschedule, so FE
 *  surfaces gate that behaviour on `bookingSource === 'instant'`, never on
 *  `projectKey === 'portal_helpline'`. Kept nullish for legacy/test rows. */
export const bookingSourceEnum = z.enum(['portal', 'offline', 'instant']);
export type BookingSource = z.infer<typeof bookingSourceEnum>;

export type { ConcernKey as Concern } from '../constants/concerns';

/**
 * Session record. BE returns FLAT denormalized fields with joined names
 * (`clientName`, `practitionerName`, etc.) rather than nested objects.
 * Mirrors `services/sessionService.ts` listSessions + getSessionById SELECTs.
 * Detail adds `organizationName` + `locationName`. `concerns` is the multi-select
 * categorical concern array (BE: Session.concerns pgEnum array).
 */
export const sessionSchema = z.object({
  id: z.string(),
  // Null on anonymous helpline sessions (a call with no usable number).
  clientId: z.string().nullable(),
  practitionerId: z.string(),
  organizationId: z.string().nullish(),
  locationId: z.string().nullish(),
  businessAssociateId: z.string().nullish(),
  sessionType: sessionTypeEnum,
  sessionMode: sessionModeEnum,
  /** Authoritative project discriminator. BE list+detail SELECT after the
   *  2026-05-08 BE patch (`feat(api): include projectKey in listSessions`).
   *  Falls back to projectKeyForSessionMode(mode) for legacy rows via
   *  effectiveProjectKey() helper — portal_helpline rows are
   *  sessionMode='online' and indistinguishable from campus_support online
   *  without this field. */
  projectKey: z.enum(['campus_support', 'helpline_support', 'portal_helpline'] as const).nullish(),
  status: sessionStatusEnum,
  scheduledStart: z.string(),
  scheduledEnd: z.string(),
  actualStart: z.string().nullish(),
  actualEnd: z.string().nullish(),
  // BE always returns an array (default empty). nullish() guards any legacy/test fixture
  // that hasn't been migrated yet.
  concerns: z.array(concernEnum).nullish(),
  /** Server-computed: may THIS viewer retag the concerns on this session?
   *  True only for a completed session viewed by its clinical owner, that
   *  owner's lead at (locationId, projectKey), a chief at the location, or
   *  super_admin. The FE cannot derive it: lead status lives in
   *  PractitionerAssignmentProject.isLead, which the payload does not carry.
   *
   *  Detail-endpoint only (computing it per row would be an N+1 on the rail).
   *  ABSENT, not false, for clients / customer_support / org_user, and that
   *  distinction is load-bearing: present-and-false means "staff who may not
   *  edit this one" (show the control disabled, with a reason), absent means
   *  "not staff" (show no control at all). */
  canEditConcerns: z.boolean().nullish(),
  // How the row originated. Logical values are `portal` (scheduled, campus or
  // portal), `offline` (backfill), and `instant` (portal_helpline click-to-call).
  // Kept as a permissive string + nullish so legacy/test rows (e.g.
  // `client_portal`) still parse; `bookingSourceEnum` is the canonical set and
  // `isInstantSession()` is the only place FE behaviour keys off `instant`.
  bookingSource: z.string().nullish(),
  bookedByRole: z.string().nullish(),
  consentGiven: z.boolean().nullish(),
  cancellationReason: z.string().nullish(),
  cancelledAt: z.string().nullish(),
  // Denormalized joins (always present in admin reads)
  clientName: z.string().nullish(),
  clientEmail: z.string().nullish(),
  practitionerName: z.string().nullish(),
  practitionerEmail: z.string().nullish(),
  // Free-text role label (e.g. "Lead Psychologist"). Optional; empty for
  // most rows since the BE primarily exposes coarse `practitionerKind`.
  practitionerRole: z.string().nullish(),
  // Coarse role surfaced by the BE on session list rows. Drives the
  // "Listener" / "Psychologist" badge under the practitioner name. Mirrors
  // sessionService.ts:706 in elixaa-core (`chief_psychologist` collapses
  // to `psychologist` so org hierarchy isn't leaked to clients).
  practitionerKind: z.enum(['volunteer', 'psychologist']).nullish(),
  // Detail-only joins
  organizationName: z.string().nullish(),
  locationName: z.string().nullish(),
  // Staff-only: the client's current risk, null when they have none yet.
  clientRiskProfile: riskProfileEnum.nullish(),
  // Telephony (Ozonetel). Null or absent for every non-telephony session, and
  // absent on list rows: `telephonyCall` is a detail-only join. The enums are
  // the telephony feature's so the two surfaces cannot drift apart.
  sessionCategory: sessionCategoryEnum.nullish(),
  callDirection: telephonyDirectionEnum.nullish(),
  telephonyCall: z
    .object({
      id: z.string(),
      direction: telephonyDirectionEnum,
      callerNumber: z.string().nullable(),
      did: z.string(),
      ringStartedAt: z.string().nullable(),
      answeredAt: z.string().nullable(),
      endedAt: z.string().nullable(),
      connectedSeconds: z.number().int().nullable(),
      callStatus: z.string().nullable(),
      recordingSyncStatus: recordingSyncEnum,
      claimedById: z.string().nullable(),
    })
    .nullish(),
  createdAt: z.string(),
  updatedAt: z.string().nullish(),
});
export type Session = z.infer<typeof sessionSchema>;

/**
 * BE list query params (sessionRoute.ts:listSessionsQuerySchema).
 * `organizationId` / `locationId` are wired to the global tenant scope
 * picker in the topbar. BA filter on the platform surface is not
 * supported by the BE.
 */
export interface SessionListParams {
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'scheduledStart' | 'status';
  sortOrder?: 'asc' | 'desc';
  from?: string; // YYYY-MM-DD
  to?: string;
  // Accepts a single status or a list; the BE's listSessionsQuerySchema is a
  // multi-enum (?status=a&status=b), and axios's paramsSerializer in
  // api-client.ts uses `indexes: null` so array values serialize as repeated
  // keys. Used by the client Upcoming tab to fetch both 'scheduled' AND
  // 'in_progress' so live sessions remain rejoinable after the BE flips a
  // joined session to in_progress.
  status?: SessionStatus | SessionStatus[];
  sessionType?: SessionType;
  sessionMode?: SessionMode;
  practitionerId?: string;
  clientId?: string;
  organizationId?: string;
  locationId?: string;
  /** Narrows to a single project (approval-routing / reporting axis). */
  projectKey?: ProjectKey;
  /** Telephony report axes (FR-10). */
  sessionCategory?: SessionCategory;
  callDirection?: CallDirection;
  redFlagStatus?: RedFlagFilter;
}

export interface PaginatedSessions {
  data: Session[];
  pagination: { page: number; limit: number; total: number };
}

// ─── Metrics ───────────────────────────────────────────────────────────────

/**
 * Filter shape for `GET /sessions/metrics`. Same axes as the list endpoint
 * minus pagination / sort / search. The enum axes are arrays for parity with
 * the list endpoint's multi-enum query params.
 */
export interface SessionMetricsParams {
  status?: SessionStatus[];
  sessionType?: SessionType[];
  sessionMode?: SessionMode[];
  bookedByRole?: ('client' | 'support' | 'psychologist')[];
  clientId?: string;
  practitionerId?: string;
  organizationId?: string;
  locationId?: string;
  projectKey?: ProjectKey;
  sessionCategory?: SessionCategory;
  callDirection?: CallDirection;
  redFlagStatus?: RedFlagFilter;
  from?: string; // YYYY-MM-DD
  to?: string; // YYYY-MM-DD
}

/**
 * `GET /sessions/metrics` response. `dateRange` echoes the requested window
 * only when BOTH `from` and `to` were supplied; `null` otherwise ("All time").
 */
export const sessionMetricsResponseSchema = z.object({
  total: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  cancelledOrNoShow: z.number().int().nonnegative(),
  dateRange: z.object({ from: z.string(), to: z.string() }).nullable(),
});
export type SessionMetricsResponse = z.infer<typeof sessionMetricsResponseSchema>;

// ─── Notes ───────────────────────────────────────────────────────────────

export const noteStatusEnum = z.enum(['draft', 'submitted']);
export type NoteStatus = z.infer<typeof noteStatusEnum>;

/** Anxiety qualitative levels — matches the BE doc (4_clinical-docs.md §3). */
export const anxietyLevelEnum = z.enum(['none', 'mild', 'moderate', 'severe']);
export type AnxietyLevel = z.infer<typeof anxietyLevelEnum>;

/** Risk levels — `critical` triggers a "raise red flag?" inline hint in the
 *  clinicalData ribbon (FE only; the BE has no enforcement). */
export const riskLevelEnum = z.enum(['none', 'low', 'medium', 'high', 'critical']);
export type RiskLevel = z.infer<typeof riskLevelEnum>;

/** Structured clinical metadata captured alongside the prose note. The BE
 *  column is freeform JSONB and the doc spells the keys snake_case (see
 *  `4_clinical-docs.md` §3 ClinicalData interface), so we mirror that to
 *  stay wire-compatible with notes created by other tools (the AI service
 *  in particular). The UI surfaces these specific keys; anything outside
 *  this set is preserved on read via `.loose()` (zod's passthrough). */
export const clinicalDataSchema = z
  .object({
    mood: z.number().int().min(1).max(10).nullish(),
    energy: z.number().int().min(1).max(10).nullish(),
    anxiety_level: anxietyLevelEnum.nullish(),
    techniques_used: z.array(z.string().min(1).max(100)).nullish(),
    homework_given: z.string().max(2_000).nullish(),
    // presenting_concerns moved to Session.concerns (categorical enum array). Kept off
    // the JSONB blob to avoid two parallel concern surfaces drifting apart.
    risk_level: riskLevelEnum.nullish(),
    next_session_goals: z.string().max(2_000).nullish(),
  })
  .loose();
export type ClinicalData = z.infer<typeof clinicalDataSchema>;

/**
 * Slim projection of `sessionNoteSchema` used by the timeline feed. Drops
 * `content` and `clinicalData` (the heavy encrypted/JSONB payload) so the
 * workspace pane can fan out one fetch per session without pulling kilobytes
 * of prose. Full content is fetched on demand when the user clicks into the
 * note editor.
 *
 * BE contract: `GET /sessions/:id/notes?meta=1` projects only these fields.
 * Until the BE ships the param handler the request still returns the full
 * row — zod strips unknown fields so this schema remains wire-compatible.
 */
export const sessionNoteMetaSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  authorId: z.string().nullish(),
  status: noteStatusEnum,
  transcriptJobId: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string().nullish(),
  authorName: z.string().nullish(),
});
export type SessionNoteMeta = z.infer<typeof sessionNoteMetaSchema>;

/** Returns a single note OR null when none exists (not a 404). */
export const sessionNoteSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  authorId: z.string().nullish(),
  status: noteStatusEnum,
  content: z.string().nullish(),
  // Use a permissive parse on the wire (the BE column is freeform JSONB)
  // and let the FE editor coerce via `clinicalDataSchema` at the form
  // boundary. Strict parse here would reject older notes with extra keys.
  clinicalData: z.unknown().nullish(),
  transcriptJobId: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string().nullish(),
  // Denormalized join — BE returns the author's display name so the timeline
  // can show "by X" without a second round-trip. Optional until the BE ships
  // the join; nullish on rows where authorId itself is null.
  authorName: z.string().nullish(),
});
export type SessionNote = z.infer<typeof sessionNoteSchema>;

// ─── Case reports ────────────────────────────────────────────────────────

// Per a690359 §3.5, BE dropped 'escalated' from the case_report_status enum.
// The lead → chief handoff is now implicit on lead-approval (status stays
// 'submitted', reviewerId flips to the chief). FE no longer surfaces a
// distinct escalated state.
export const caseReportStatusEnum = z.enum(['draft', 'submitted', 'approved', 'rejected']);
export type CaseReportStatus = z.infer<typeof caseReportStatusEnum>;

/**
 * Slim projection of `caseReportSchema` used by the timeline feed. Drops
 * `content`, `rejectionReason`, and `reportFileUrl` so a session with many
 * reports still produces a small payload. The full row loads when the user
 * opens the case-report detail page.
 *
 * BE contract: `GET /sessions/:id/case-reports?meta=1` projects only these
 * fields. Until the BE ships the param handler the request still returns the
 * full row — zod strips unknown fields so this schema remains wire-compatible.
 */
export const caseReportMetaSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  clientId: z.string().nullish(),
  authorId: z.string().nullish(),
  status: caseReportStatusEnum,
  reviewerId: z.string().nullish(),
  approvedById: z.string().nullish(),
  approvalDate: z.string().nullish(),
  transcriptJobId: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string().nullish(),
  authorName: z.string().nullish(),
  reviewerName: z.string().nullish(),
  approvedByName: z.string().nullish(),
});
export type CaseReportMeta = z.infer<typeof caseReportMetaSchema>;

/** Returns an array (empty when none — not a 404). */
export const caseReportSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  clientId: z.string().nullish(),
  authorId: z.string().nullish(),
  status: caseReportStatusEnum,
  content: z.string().nullish(),
  reviewerId: z.string().nullish(),
  approvedById: z.string().nullish(),
  approvalDate: z.string().nullish(),
  rejectionReason: z.string().nullish(),
  reportFileUrl: z.string().nullish(),
  // Links the report back to the AI transcript job that drafted it (when
  // applicable). Drives the "AI-drafted" banner in CaseReportEditor.
  // Per `4_clinical-docs.md` §2 (CaseReport interface).
  transcriptJobId: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string().nullish(),
  // Denormalized joins — BE projects the author / reviewer / approver display
  // names so the timeline row can surface "X submitted · pending Y" /
  // "Approved by Z" without follow-up requests. All three are nullish: drafts
  // have no reviewer, only approved rows have an approver, and legacy rows
  // pre-BE-join will simply omit them.
  authorName: z.string().nullish(),
  reviewerName: z.string().nullish(),
  approvedByName: z.string().nullish(),
});
export type CaseReport = z.infer<typeof caseReportSchema>;

// ─── Feedback ────────────────────────────────────────────────────────────

/** Per-axis ratings (1-5 each). Spec assumed a single rating; BE stores an object. */
export const sessionFeedbackRatingsSchema = z.object({
  overall: z.number().int().min(1).max(5).nullish(),
  heard: z.number().int().min(1).max(5).nullish(),
  clarity: z.number().int().min(1).max(5).nullish(),
  comfort: z.number().int().min(1).max(5).nullish(),
});
export type SessionFeedbackRatings = z.infer<typeof sessionFeedbackRatingsSchema>;

/** Returns an array (empty when none — not a 404). */
export const sessionFeedbackSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  clientId: z.string().nullish(),
  ratings: sessionFeedbackRatingsSchema.nullish(),
  comment: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string().nullish(),
});
export type SessionFeedback = z.infer<typeof sessionFeedbackSchema>;

// ─── Case report rejection input ────────────────────────────────────────

export const rejectCaseReportSchema = z.object({
  rejectionReason: z.string().min(1).max(500),
});

// ---------------------------------------------------------------------------
// Clinical bundle (GET /sessions/:id/clinical-bundle)
// Mirrors elixaa-api/src/services/sessionBundleService.ts:315-343.
// ---------------------------------------------------------------------------

export const sessionSummaryStatusSchema = z.enum([
  'in_progress',
  'complete',
  'failed',
  'disabled',
  'no_transcript',
  'not_applicable',
]);
export type SessionSummaryStatus = z.infer<typeof sessionSummaryStatusSchema>;

const bundleSessionSchema = z.object({
  id: z.string(),
  organizationId: z.string().nullish(),
  practitionerId: z.string(),
  clientId: z.string().nullish(),
  locationId: z.string().nullish(),
  projectKey: z.string().nullish(),
  sessionType: z.string(),
  sessionMode: sessionModeEnum,
  status: sessionStatusEnum,
  scheduledStart: z.string(),
  actualStart: z.string().nullish(),
  actualEnd: z.string().nullish(),
  actualDurationMinutes: z.number().nullish(),
});
export type BundleSession = z.infer<typeof bundleSessionSchema>;

const bundleCaseReportSchema = z.object({
  id: z.string(),
  status: z.enum(['draft', 'submitted', 'approved', 'rejected']),
  authorId: z.string(),
  content: z.string().nullish(),
  transcriptJobId: z.string().nullish(),
  rejectionReason: z.string().nullish(),
});
export type BundleCaseReport = z.infer<typeof bundleCaseReportSchema>;

const bundleSessionNoteSchema = z.object({
  id: z.string(),
  status: z.enum(['draft', 'submitted']),
  authorId: z.string(),
  content: z.string().nullish(),
  transcriptJobId: z.string().nullish(),
});
export type BundleSessionNote = z.infer<typeof bundleSessionNoteSchema>;

const bundleTranscriptSegmentSchema = z.object({
  segmentId: z.string(),
  speakerRole: z.string(),
  text: z.string(),
  startMs: z.number(),
  endMs: z.number(),
  language: z.string(),
});
export type BundleTranscriptSegment = z.infer<typeof bundleTranscriptSegmentSchema>;

const bundleRecordingSchema = z.object({
  key: z.string(),
  downloadUrl: z.string(),
  expiresAt: z.string(),
});
export type BundleRecording = z.infer<typeof bundleRecordingSchema>;

const generationSchema = z.object({
  status: sessionSummaryStatusSchema,
  streamUrl: z.string().nullable(),
});
export type Generation = z.infer<typeof generationSchema>;

export const clinicalBundleSchema = z.object({
  session: bundleSessionSchema,
  caseReports: z.array(bundleCaseReportSchema),
  sessionNote: bundleSessionNoteSchema.nullable(),
  transcript: z.object({ segments: z.array(bundleTranscriptSegmentSchema) }),
  recordings: z.array(bundleRecordingSchema),
  generation: generationSchema,
});
export type ClinicalBundle = z.infer<typeof clinicalBundleSchema>;
